'use client'
import * as React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

type Choice = { label: string; score: number }
type Question = { id: string; q: string; choices: Choice[] }

const questions: Question[] = [
  {
    id: 'platform',
    q: 'What ecommerce platform are you on?',
    choices: [
      { label: 'Shopify', score: 1 },
      { label: 'WooCommerce', score: 1 },
      { label: 'BigCommerce or Wix', score: 1 },
      { label: 'Custom / headless / Hydrogen', score: 1 },
    ],
  },
  {
    id: 'sources',
    q: 'Where do customers post about you today?',
    choices: [
      { label: 'Instagram + TikTok', score: 3 },
      { label: 'Just Instagram', score: 2 },
      { label: 'YouTube + reviews on Trustpilot / Google', score: 3 },
      { label: 'Pinterest, X, LinkedIn, Threads', score: 2 },
    ],
  },
  {
    id: 'volume',
    q: 'Roughly how many monthly mentions / tags / reviews?',
    choices: [
      { label: 'Under 50', score: 1 },
      { label: '50–500', score: 2 },
      { label: '500–5,000', score: 3 },
      { label: '5,000+', score: 4 },
    ],
  },
  {
    id: 'team',
    q: 'Who would run the UGC programme?',
    choices: [
      { label: 'Solo founder / marketer', score: 1 },
      { label: 'Internal team (2–5 people)', score: 2 },
      { label: 'Agency partner', score: 3 },
      { label: 'Multi-brand marketing org', score: 4 },
    ],
  },
  {
    id: 'urgency',
    q: 'What’s your driver?',
    choices: [
      { label: 'Lift PDP conversion', score: 3 },
      { label: 'Cut studio photo costs', score: 2 },
      { label: 'Replace Bazaarvoice / Tolstoy / Videowise', score: 4 },
      { label: 'Just exploring', score: 1 },
    ],
  },
]

export function UgcQuiz() {
  // Answers are keyed by question id; we store the chosen choice label
  // so "selected" comparisons are unambiguous. Scoring derives from the
  // label by lookup in `questions`. Previously we stored just the score,
  // which made every option that shared a score (e.g. all four platform
  // options with score 1) appear selected at once.
  const [answers, setAnswers] = React.useState<Record<string, string>>({})
  const [done, setDone] = React.useState(false)
  const resultRef = React.useRef<HTMLDivElement | null>(null)
  const scoreFor = (qid: string, label: string) =>
    questions.find((q) => q.id === qid)?.choices.find((c) => c.label === label)?.score ?? 0
  const total = Object.entries(answers).reduce((sum, [qid, label]) => sum + scoreFor(qid, label), 0)
  const allAnswered = Object.keys(answers).length === questions.length

  function handleSubmit() {
    setDone(true)
    // Defer to the next frame so the result block mounts before we scroll.
    requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  // Scoring thresholds mirror ugcQuizPlan() in nextapp/lib/publicToolsMath.ts
  // (nextapp's white-label copy for Feefo, see this repo's CLAUDE.md "Public
  // calculator tools"). The question set above is copy, not a formula, so
  // it's duplicated directly in nextapp's own component rather than pulled
  // from that shared file — keep the two question arrays worded the same if
  // you edit this one.
  function pickPlan() {
    if (total >= 14) return { plan: 'Agency', price: '$299/mo', why: 'Multi-brand or high-volume UGC needs the agency portal, white-label and 10 sub-accounts.' }
    if (total >= 9)  return { plan: 'Growth',  price: '$199/mo', why: 'You’ve got the volume + driver to use rights management, AI tagging at scale and revenue attribution.' }
    return            { plan: 'Starter', price: '$99/mo',  why: 'Get the gallery + AB testing + AI tagging running on your top PDPs first; upgrade once you outgrow it.' }
  }
  function pickLayout() {
    const sources = scoreFor('sources', answers['sources'] ?? '')
    if (sources >= 3) return 'Stories + Carousel'
    if (total >= 12) return 'Lookbook / Flipbook'
    return 'Grid + Mini-frame'
  }
  const result = pickPlan()

  if (!done || !allAnswered) {
    return (
      <div className="space-y-8">
        {questions.map((q) => (
          <div key={q.id} className="rounded-md border bg-card p-6">
            <p className="font-display text-lg font-medium">{q.q}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {q.choices.map((c) => {
                const selected = answers[q.id] === c.label
                return (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: c.label }))}
                    className={`rounded-md border px-4 py-3 text-left text-sm transition ${
                      selected ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background hover:border-primary/40'
                    }`}
                  >
                    {c.label}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
        <Button size="lg" disabled={!allAnswered} onClick={handleSubmit}>
          {allAnswered ? 'See my recommendation' : `Answer the rest (${questions.length - Object.keys(answers).length} left)`}
        </Button>
      </div>
    )
  }

  return (
    <div ref={resultRef} className="rounded-md border bg-card p-8 scroll-mt-24">
      <p className="text-sm uppercase tracking-wide text-muted-foreground">Your recommendation</p>
      <h2 className="mt-2 font-display text-3xl font-medium md:text-4xl">
        Start with <span className="text-primary">{result.plan}</span> at {result.price}.
      </h2>
      <p className="mt-3 text-muted-foreground text-pretty">{result.why}</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Block title="First widget to ship" body={pickLayout()} />
        <Block title="Conservative monthly lift" body={`+${Math.min(40, 8 + total * 1.6).toFixed(0)}% on PDPs that adopt UGC`} />
      </div>

      <ol className="mt-8 space-y-3">
        <li className="flex gap-3"><Step n="1" /> Connect your store (Shopify / Woo / BigCommerce / Wix / HTML).</li>
        <li className="flex gap-3"><Step n="2" /> Auto-import the last 90 days from your top three social sources.</li>
        <li className="flex gap-3"><Step n="3" /> Drop the {pickLayout()} widget on a single PDP and run a 14-day A/B vs control.</li>
      </ol>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button asChild size="lg"><Link href="https://app.idukki.io/register">Start the {result.plan} plan free for 14 days</Link></Button>
        <Button variant="outline" size="lg" onClick={() => { setDone(false); setAnswers({}) }}>Retake quiz</Button>
      </div>
    </div>
  )
}

function Block({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border bg-background p-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-2 font-display text-lg font-medium">{body}</p>
    </div>
  )
}
function Step({ n }: { n: string }) {
  return <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 font-display text-sm font-medium text-primary">{n}</span>
}
